import React from 'react';
import styled from 'styled-components';
import { Flag, Coins, CalendarRange, Circle } from 'lucide-react';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import { Card, Muted, Section } from '../parts';
import { MONETIZATION_LEVELS } from '../labels';
import { ConsultingCallout } from '../consulting/ConsultingBanner';

export default function PlanTab({ analysis, consulting, onSchedule }) {
    const { plan } = analysis.result;

    return (
        <>
            <Section icon={Flag} title='Por onde começar' subtitle={plan.verdict}>
                <Card>
                    <Intro>{plan.intro}</Intro>
                    <Steps>
                        {plan.priority_actions.map((action, index) => (
                            <Step key={index}>
                                <StepNumber>{index + 1}</StepNumber>
                                <div>
                                    <StepTitle>{action.title}</StepTitle>
                                    <Muted>{action.description}</Muted>
                                </div>
                            </Step>
                        ))}
                    </Steps>
                </Card>
            </Section>

            {plan.monetization.length > 0 && (
                <Section icon={Coins} title='Como o perfil vira receita' subtitle='Do conteúdo gratuito ao ingresso vendido'>
                    <Lanes>
                        {MONETIZATION_LEVELS.map(level => {
                            const items = plan.monetization.filter(item => item.level === level.key);

                            return (
                                <Lane key={level.key}>
                                    <LaneHead>
                                        <strong>{level.label}</strong>
                                        <span>{level.hint}</span>
                                    </LaneHead>
                                    {items.map((item, index) => (
                                        <LaneItem key={index} $future={item.future}>
                                            <LaneTitle>
                                                {item.title}
                                                {item.future && <FutureTag>mais adiante</FutureTag>}
                                            </LaneTitle>
                                            <LaneMeta>{item.price} · {item.channel}</LaneMeta>
                                        </LaneItem>
                                    ))}
                                    {items.length === 0 && <Muted>Nada por enquanto.</Muted>}
                                </Lane>
                            );
                        })}
                    </Lanes>
                </Section>
            )}

            <Section icon={CalendarRange} title='Os próximos 90 dias'>
                <Timeline>
                    {plan.phases.map((phase, index) => (
                        <Phase key={index}>
                            <PhaseMarker />
                            <PhaseBody>
                                <PhasePeriod>{phase.period}</PhasePeriod>
                                <PhaseTitle>{phase.title}</PhaseTitle>
                                <Tasks>
                                    {phase.tasks.map((task, taskIndex) => (
                                        <Task key={taskIndex}>
                                            <Circle size={12} />
                                            <span>{task}</span>
                                        </Task>
                                    ))}
                                </Tasks>
                            </PhaseBody>
                        </Phase>
                    ))}
                </Timeline>
            </Section>

            <ConsultingCallout consulting={consulting} onSchedule={onSchedule} />
        </>
    );
}

const Intro = styled.p`
    margin: 0 0 16px;
    white-space: pre-line;
    font-size: 14px;
    line-height: 1.6;
    color: ${colors.nightDark};
`;

const Steps = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const Step = styled.div`
    display: flex;
    gap: 12px;
    align-items: flex-start;
`;

const StepNumber = styled.span`
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    color: ${colors.neutralLight};
    background: ${panelAccent(colors.secondaryPurple)};
`;

const StepTitle = styled.p`
    margin: 3px 0 2px;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Lanes = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;

    @media screen and (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const Lane = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px;
    border-radius: 12px;
    background: ${colors.neutralBackground};
`;

const LaneHead = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 4px;

    strong {
        font-size: 14px;
        color: ${colors.nightDark};
    }

    span {
        font-size: 12px;
        color: ${colors.nightLight};
    }
`;

const LaneItem = styled.div`
    padding: 10px 12px;
    border-radius: 10px;
    background: ${colors.neutralLight};
    border: 1px ${({ $future }) => ($future ? 'dashed' : 'solid')} ${colors.night}26;
    opacity: ${({ $future }) => ($future ? 0.8 : 1)};
`;

const LaneTitle = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const FutureTag = styled.span`
    font-size: 11px;
    font-weight: 500;
    color: ${colors.nightLight};
`;

const LaneMeta = styled.span`
    font-size: 12px;
    color: ${colors.night};
`;

const Timeline = styled.div`
    display: flex;
    flex-direction: column;
`;

const Phase = styled.div`
    display: grid;
    grid-template-columns: 18px 1fr;
    gap: 14px;
    position: relative;
    padding-bottom: 20px;

    &::before {
        content: '';
        position: absolute;
        left: 8px;
        top: 18px;
        bottom: 0;
        width: 2px;
        background: ${panelAccentAlpha(colors.secondaryPurple, '33')};
    }

    &:last-child::before {
        display: none;
    }
`;

const PhaseMarker = styled.span`
    width: 18px;
    height: 18px;
    border-radius: 50%;
    margin-top: 2px;
    border: 3px solid ${panelAccent(colors.secondaryPurple)};
    background: ${colors.neutralLight};
`;

const PhaseBody = styled(Card)`
    padding: 14px 16px;
`;

const PhasePeriod = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${panelAccent(colors.secondaryPurple)};
`;

const PhaseTitle = styled.p`
    margin: 2px 0 8px;
    font-size: 15px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Tasks = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Task = styled.div`
    display: flex;
    gap: 8px;
    align-items: flex-start;
    font-size: 13px;
    line-height: 1.5;
    color: ${colors.night};

    svg {
        flex-shrink: 0;
        margin-top: 4px;
        color: ${colors.nightLight};
    }
`;
